import React from "react";
import PropTypes from "prop-types";
import radium, { Style } from "radium";
import shallowCompare from "react-addons-shallow-compare";
import leaflet from "leaflet";
import assign from "object-assign";
import { zIndex } from "rizzo-next/sass/settings.json";
import { connect } from "react-redux";
import actions from "../../actions";
import { createDetailUrlFromResource } from "../../../../../server/utils/createUrls";
import { styles, scopedStyles, getMarkerStyles } from "./styles";
import icons from "./icons";

const markers = {
  sleeping: "sleep",
  sleepingPrice: "sleep-price",
  drinking_nightlife: "drink",
  transport: "transport",
  activities: "see",
  tours: "see",
  entertainment: "play",
  shopping: "shop",
  eating: "eat",
  restaurants: "eat",
  sights: "see",
  info: "default",
  festivals_events: "play",
};

export default class InteractiveMap extends React.Component {
  static getCustomSvgIcon(icon, markerSize, isActive = false) {
    const activeClass = isActive ? "is-active" : "";
    const svgIcons = {
      default: {
        className: `leaflet-div-icon-default ${activeClass}`,
        iconSize: markerSize,
        html: icons.defaultIcon,
      },

      center: {
        className: `leaflet-div-icon-center ${activeClass}`,
        iconSize: [30, 30],
        html: icons.defaultIcon,
      },

      eat: {
        className: `leaflet-div-icon-eat ${activeClass}`,
        iconSize: markerSize,
        html: icons.eat,
      },

      drink: {
        className: `leaflet-div-icon-drink ${activeClass}`,
        iconSize: markerSize,
        html: icons.drink,
      },

      play: {
        className: `leaflet-div-icon-play ${activeClass}`,
        iconSize: markerSize,
        html: icons.play,
      },

      see: {
        className: `leaflet-div-icon-see ${activeClass}`,
        iconSize: markerSize,
        html: icons.see,
      },

      shop: {
        className: `leaflet-div-icon-shop ${activeClass}`,
        iconSize: markerSize,
        html: icons.shop,
      },

      sleep: {
        className: `leaflet-div-icon-sleep ${activeClass}`,
        iconSize: markerSize,
        html: icons.sleep,
      },

      transport: {
        className: `leaflet-div-icon-transport ${activeClass}`,
        iconSize: markerSize,
        html: icons.transport,
      },
    };

    return leaflet.divIcon(svgIcons[icon]);
  }

  static getPriceMarker(html, isActive = false) {
    return leaflet.divIcon({
      className: `leaflet-div-icon-price ${isActive ? "is-active" : ""}`,
      html,
      // Hacky way to correctly position the price icon (which has an auto width)
      iconAnchor: leaflet.point(8 + (html.length * 4), 10),
    });
  }

  /**
   * Compares places arrays, returns true if the arrays have changed order or price
   * @param  {array} nextPlaces     Updated array of places
   * @param  {array} currentPlaces  Current array of places
   */
  static placesDataUpdated(nextPlaces, currentPlaces) {
    return nextPlaces.some((place, index) => {
      const currentPlace = currentPlaces[index];
      if (currentPlace) {
        return place.id !== currentPlace.id || place.price !== currentPlace.price;
      }

      return true;
    });
  }

  constructor(props) {
    super(props);

    this.state = {
      popupBanner: {
        isVisible: false,
        name: "",
        subtype: "",
      },
      clickedMarker: {},
      currentPoi: null,
      showAttribution: false,
    };

    this.mapSettings = {
      accessToken: "pk.eyJ1IjoibG9uZWx5cGxhbmV0IiwiYSI6Imh1ODUtdUEifQ.OLLon0V6rcoTyayXzzUzsg",
      attribution: `<span title="Map data &copy; OpenStreetMap contributors,
        CC-BY-SA, Imagery &copy; Mapbox">&copy;</span>`,
      maxZoom: 17,
      popupOptions: {
        className: "InteractiveMap-popup",
        closeButton: false,
        maxWidth: 150,
        offset: leaflet.point(0, 0),
        autoPan: false,
      },
      projectId: "lonelyplanet.b963d424",
      url: "https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}",
    };

    [
      "setMarkerIcon",
      "initializeMap",
      "initializeTileLayer",
      "initializeCenterMarker",
      "initializeMarkers",
      "initializePopup",
      "updateMarkerForHoverMode",
      "updateMarkerForExploreMode",
      "fitBounds",
      "toggleAttribution",
      "getMapIcon",
    ].forEach(fn => {
      this[fn] = this[fn].bind(this);
    });
  }

  componentWillMount() {
    this.markerStyles = getMarkerStyles(this.props.mode);
  }

  componentDidMount() {
    const {
      center,
      centerName,
      places,
      hideCenterPin,
      mapId,
      markerClick,
      zoom,
      hideZoomControls,
    } = this.props;

    this.initializeMap(mapId, center, zoom, hideZoomControls);

    this.initializeTileLayer();
    if (!hideCenterPin && center && centerName) {
      this.initializeCenterMarker(center, centerName);
    }

    this.initializeMarkers(places, markerClick);
  }

  componentWillReceiveProps(nextProps) {
    const {
      mode,
      activePoi,
      markerSize,
      markerClick,
    } = this.props;

    // Check for new POIs when:
    // - type changes
    // - list is filtered
    // - scroll to load more
    const { places: nextPlaces } = nextProps;
    const { places: currentPlaces } = this.props;
    if (nextPlaces.length !== currentPlaces.length
      || InteractiveMap.placesDataUpdated(nextPlaces, currentPlaces)) {
      this.leafletMap.removeLayer(this.layerGroup);
      this.initializeMarkers(nextProps.places, markerClick);
    }

    if (mode === "hover") {
      this.updateMarkerForHoverMode(nextProps);
    }

    if (mode === "explore" && activePoi.id !== nextProps.activePoi.id) {
      this.updateMarkerForExploreMode(activePoi, markerSize, nextProps);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  componentWillUpdate(nextProps, nextState) {
    if (this.props.mode === "explore") {
      const markerShouldUpdate = nextState.clickedMarker &&
        this.state.currentPoi &&
        this.state.clickedMarker !== nextState.clickedMarker;
      if (markerShouldUpdate) {
        if (this.state.clickedMarker.target !== nextState.clickedMarker.target) {
          this.setMarkerIcon();
        } else {
          window.location.href = this.setPoiUrl(nextProps.activePoi);
        }
      }
    }
  }

  /**
   * Gets the url for the poi details page
   * @param  {object} poi  Data for the "current" POI
   */
  setPoiUrl(poi) {
    return this.props.poiDetailsHost + createDetailUrlFromResource(
      poi,
      this.props.breadcrumbs.map(b => b.title),
      poi.params.placeId,
    );
  }

  /**
   * Updates an icon's marker when it is selected or deselected
   * @param  {array}   markerSize Width and height of the marker icon
   * @param  {boolean} isActive   True if the marker is selected
   */
  setMarkerIcon(markerSize = this.props.markerSize, isActive = false) {
    const marker = this.state.clickedMarker.target;
    const getIcon = this.getMapIcon(markerSize, isActive);

    if (!marker) return;
    this.layerGroup.eachLayer((layer) => {
      if (marker.options.id === layer.options.id) {
        layer.setIcon(getIcon(this.state.currentPoi));
      }
    });
  }

  /**
   * Returns a function to fetch the correct marker for the type of poi
   * @param  {array}   markerSize Width and height of the marker icon
   * @param  {boolean} isActive   True if the marker is selected
   */
  getMapIcon(markerSize = this.props.markerSize, isActive = false) {
    // If we are not displaying the price as a marker, return the correct svg marker
    if (!this.props.displayPriceMarker) {
      return (poi) => InteractiveMap.getCustomSvgIcon(
        [markers[poi.poiType.toLowerCase()]],
        markerSize,
        isActive
      );
    }

    return (poi) => {
      // If the user hasn't entered dates yet, show the correct svg marker
      if (!poi.price && !poi.soldOut) {
        return InteractiveMap.getCustomSvgIcon(
          [markers[poi.poiType.toLowerCase()]],
          markerSize,
          isActive
        );
      }

      // Display the poi price or booked status
      return InteractiveMap.getPriceMarker(
        poi && poi.price ? poi.price : "FULL",
        isActive,
      );
    };
  }

  /**
   * Initialize leaflet.map
   * @see http://leafletjs.com/reference.html#map-class
   * @param  {string}  id               ID in the DOM on which to attach the map to
   * @param  {array}   center           Initial geographical center of the map
   * @param  {number}  zoom             Initial map zoom
   * @param  {boolean} hideZoomControls Whether the zoom control is added to the map by default
   * @return {object}                   Instantiates a map object given a div element (or its id)
   *                                    and optionally an object literal with map options
   */
  initializeMap(id, center, zoom, hideZoomControls) {
    this.leafletMap = leaflet.map(id, {
      scrollWheelZoom: false,
      zoomControl: !hideZoomControls,
      attributionControl: false,
    });

    /**
     * Reset the state and clicked marker's size when the map is clicked on,
     * i.e. when a marker is clicked off of
     */
    this.leafletMap.on("click dragstart", () => {
      const newState = {
        showAttribution: false,
      };

      if (this.state.popupBanner.isVisible) {
        this.setMarkerIcon();

        assign(newState, {
          popupBanner: {
            isVisible: !this.state.popupBanner.isVisible,
            name: "",
            subtype: "",
          },
          clickedMarker: {},
          currentPoi: null,
        });

        this.props.dispatch(actions.hoverPlace({}));
      }

      this.setState(newState);
    });
  }

  /**
   * Initialize leaflet.tileLayer
   * @see http://leafletjs.com/reference.html#tilelayer
   * @return {object} Instantiates a tile layer object given a URL template and optionally an
   *                  options object
   */
  initializeTileLayer() {
    leaflet.tileLayer(this.mapSettings.url, {
      accessToken: this.mapSettings.accessToken,
      attribution: this.mapSettings.attribution,
      id: this.mapSettings.projectId,
      maxZoom: this.mapSettings.maxZoom,
    }).addTo(this.leafletMap);
  }

  /**
   * Display a marker at the map's center; uses a large blue icon to denote
   * @see http://leafletjs.com/reference.html#marker
   * @return {object}
   */
  initializeCenterMarker(center, centerName) {
    this.centerMarker = leaflet.marker(center, {
      opacity: 1,
      icon: InteractiveMap.getCustomSvgIcon("center"),
    }).bindPopup(centerName, {
      offset: leaflet.point(0, -5),
      autoPan: false,
    }).addTo(this.leafletMap);
  }

  /**
   * Create the map's markers from an array of places
   * @see http://leafletjs.com/reference.html#marker
   * @return {object} Instantiates a marker object given a geographical point and optionally an
   *                  options object
   */
  initializeMarkers(places, onClick) {
    const getIcon = this.getMapIcon();
    this.bounds = [];
    this.markers = places.filter(p => p.location).map((poi) => {
      const coords = poi.location.coordinates.slice(0).reverse();

      this.bounds.push(coords);

      const leafletMarker = leaflet.marker(coords, {
        opacity: this.props.markerOpacity,
        icon: getIcon(poi),
        id: `marker-${poi.id}`,
        riseOnHover: true,
      })
        .on("click", (event) => {
          this.setState({
            clickedMarker: event,
            currentPoi: poi,
            showAttribution: false,
          });

          if (typeof onClick === "function") {
            onClick(event, poi);
          }
        })
        .on("mouseover", (event) => {
          if (this.props.mode === "hover") {
            event.target.openPopup();
            event.target.setOpacity(1);
            this.props.dispatch(actions.hoverPlace(poi));
          }
        })
        .on("mouseout", (event) => {
          if (this.props.mode === "hover") {
            event.target.closePopup();
            this.props.dispatch(actions.hoverPlace({}));
          }
        });

      if (this.props.mode !== "explore") {
        leafletMarker.bindPopup(poi.name, this.mapSettings.popupOptions);
      }

      return leafletMarker;
    });

    this.layerGroup = leaflet.layerGroup(this.markers).addTo(this.leafletMap);

    if (this.props.fitBounds) {
      this.fitBounds();
    }
  }

  /**
   * Create a map popup
   * @see http://leafletjs.com/reference.html#popup
   * @return {object} Instantiates a popup object given an optional options object that describes
   *                  its appearance, location and an optional source object that is used to tag the
   *                  popup with a reference to the ILayer to which it refers
   */
  initializePopup(latlng, content) {
    this.popup = leaflet.popup(this.mapSettings.popupOptions)
      .setLatLng(latlng.slice(0).reverse())
      .setContent(content)
      .openOn(this.leafletMap);

    if (this.props.panToCenter) {
      this.leafletMap.panTo(latlng);
    }
  }

  /**
   * Update and display the marker's popup when the map is in "hover" mode
   * @param {object} nextProps The new set of props recieved from componentWillReceiveProps
   */
  updateMarkerForHoverMode(nextProps) {
    const shouldInitializePopup = nextProps.activePoi.id &&
      nextProps.activePoi.name &&
      nextProps.activePoi.location &&
      nextProps.activePoi.location.coordinates.length;

    if (shouldInitializePopup) {
      this.initializePopup(nextProps.activePoi.location.coordinates, nextProps.activePoi.name);

      this.layerGroup.eachLayer((layer) => {
        const opacityShouldUpdate = `marker-${nextProps.activePoi.id}` === layer.options.id &&
          layer.options.opacity !== 1;

        if (opacityShouldUpdate) {
          layer.setOpacity(1);
        }
      });
    }

    if (!nextProps.activePoi.id) {
      this.leafletMap.closePopup();
    }
  }

  /**
   * Update and display the marker's popup as a banner at the top of the map
   * when the map is in "explore" mode for mobile
   * @param  {object} activePoi  Data for the "current" POI
   * @param  {array}  markerSize Width and height of the marker icon
   * @param  {object} nextProps  The new set of props recieved from componentWillReceiveProps
   */
  updateMarkerForExploreMode(activePoi, markerSize, nextProps) {
    const shouldInitializePopup = nextProps.activePoi.id &&
      nextProps.activePoi.name &&
      nextProps.activePoi.location.coordinates.length;

    /**
     * Change the clicked marker's size and update the state to show the popup
     * banner
     */
    if (shouldInitializePopup) {
      this.setMarkerIcon([50, 50], true);

      this.setState({
        popupBanner: {
          isVisible: true,
          name: nextProps.activePoi.name,
          subtype: nextProps.activePoi.subtypes[0],
        },
      });

      if (this.props.panToCenter) {
        this.leafletMap.panTo(nextProps.activePoi.location.coordinates.slice(0).reverse());
      }

      this.layerGroup.eachLayer((layer) => {
        const zIndexShouldUpdate = `marker-${nextProps.activePoi.id}` === layer.options.id &&
          layer.options.zIndexOffset === 0;

        // Move a marker to the top layer if it is blocked by other markers
        if (zIndexShouldUpdate) {
          layer.setZIndexOffset(zIndex.globalHeader);
        }
      });
    }

    /**
     * If the marker is already open, go to the POI on second click/tap; this
     * mimics the functionality in Guides app
     */
    if (activePoi.id === nextProps.activePoi.id && this.state.popupBanner.isVisible) {
      window.location.href = this.setPoiUrl(activePoi);
    }
  }

  fitBounds() {
    if (this.bounds.length) {
      this.leafletMap.fitBounds(this.bounds, {
        padding: [30, 30],
      });
    }
  }

  toggleAttribution() {
    this.setState({
      showAttribution: !this.state.showAttribution,
    });
  }

  render() {
    const {
      fill,
      height,
      mapId,
      mode,
      activePoi,
    } = this.props;

    return (
      <div
        className="InteractiveMap-container"
        style={[
          styles.container.base,
          fill && styles.container.fill,
          height && { height: `${height}px` },
        ]}
      >
        <Style
          scopeSelector=".InteractiveMap-container"
          rules={assign({}, scopedStyles, this.markerStyles)}
        />

        {mode === "explore" && activePoi.id && activePoi.params &&
          <div
            className="InteractiveMap-popupBanner"
            style={[
              styles.popupBanner.container.base,
              !this.state.popupBanner.isVisible &&
                styles.popupBanner.container.hidden,
              this.state.popupBanner.isVisible && this.state.popupBanner.name &&
                styles.popupBanner.container.visible,
            ]}
          >
            <a
              href={this.setPoiUrl(activePoi)}
              style={styles.popupBanner.anchor.base}
            >
              <div
                className="InteractiveMap-popupBanner-name"
                style={styles.popupBanner.name.base}
              >
                {this.state.popupBanner.name}
              </div>

              <div
                className="InteractiveMap-popupBanner-subtype"
                style={styles.popupBanner.subtype.base}
              >
                {this.state.popupBanner.subtype}
              </div>
            </a>
          </div>
        }

        <div
          className="InteractiveMap"
          id={mapId}
          style={styles.map.base}
        />

        <div
          className="InteractiveMap-attribution"
          style={styles.attribution.container.base}
        >
          <button
            onClick={this.toggleAttribution}
            style={styles.attribution.button.base}
          >
            Attribution
          </button>
          <div
            onClick={this.toggleAttribution}
            style={[
              styles.attribution.content.base,
              this.state.showAttribution ?
                styles.attribution.content.visible :
                styles.attribution.content.hidden,
            ]}
          >
            <a href="http://leafletjs.com/">Leaflet</a> | Map data &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors, <a href="http://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="http://mapbox.com">Mapbox</a>
          </div>
        </div>
      </div>
    );
  }
}

InteractiveMap.propTypes = {
  /**
   * The place breadcrumb for the list of pois
   */
  breadcrumbs: PropTypes.array,

  /**
   * Lat and long coordinates for the initial geographical center of the map
   */
  center: PropTypes.array.isRequired,

  /**
   * An array of places to plot on the map as markers
   */
  places: PropTypes.array.isRequired,

  /**
   * Name of the POI at the center of the map
   */
  centerName: PropTypes.string,

  /**
   * Allow the map to fill its parent's dimentions
   */
  fill: PropTypes.bool,

  /**
   * Should the pin/marker for the centerpoint be hidden
   */
  hideCenterPin: PropTypes.bool,

  /**
   * Data for the current POI; expects:
   *
   * coords  Array  Lat and long coodinates of the marker that is currently active
   * name    String Name of the marker that is currently active
   * id      Number ID of POI that's selected
   * type    String Main type of the POI
   * subtype String Subtype classification of the POI
   */
  activePoi: PropTypes.object,

  /**
   * A height (in pixels) to constrain the map to
   */
  height: PropTypes.number,

  /**
   * ID to initialize the map
   */
  mapId: PropTypes.string,

  /**
   * Tell the map how it should behave when controlled by an outside component
   *
   * - hover: a list item is hovered and the corresponding marker popup's visibility is toggled
   * - explore: full-screen experience for mobile devices
   */
  mode: PropTypes.oneOf([
    "",
    "hover",
    "explore",
  ]),

  /**
   * Method to run when the marker receives the click event
   */
  markerClick: PropTypes.func,

  /**
   * Width and height for map's marker size
   */
  markerSize: PropTypes.array,

  /**
   * Opacity value for marker from 0 to 1.
   */
  markerOpacity: PropTypes.number,

  /**
   * Initial zoom level for the map
   */
  zoom: PropTypes.number,

  /**
   * Whether or not to hide the map's zoom controls
   */
  hideZoomControls: PropTypes.bool,

  /**
   * Whether or not to pan the map to a new centerpoint
   */
  panToCenter: PropTypes.bool,

  /**
   * Used to send an action for the map markers
   */
  dispatch: PropTypes.func,

  /**
   * Sets a map view that contains the given geographical bounds with the
   * maximum zoom level possible
   */
  fitBounds: PropTypes.bool,

  /**
   * Displays map markers with prices if true
   * Otherwise displays the icon for the poi type
   */
  displayPriceMarker: PropTypes.bool,

  /**
   * The base url to the poi detail pages
   */
  poiDetailsHost: PropTypes.string,
};

InteractiveMap.defaultProps = {
  breadcrumbs: [],

  center: [],

  centerName: "",

  places: [],

  fill: false,

  hideCenterPin: false,

  activePoi: {},

  height: null,

  mapId: "leaflet-map-container",

  mode: "",

  markerClick: null,

  markerSize: [20, 20],

  markerOpacity: 1,

  zoom: 18,

  hideZoomControls: false,

  panToCenter: false,

  dispatch: null,

  fitBounds: false,

  displayPriceMarker: false,

  poiDetailsHost: "https://www.lonelyplanet.com",
};

InteractiveMap.styles = styles;

const mapStateToProps = (state) => ({
  activePoi: state.nearbyPois.active || {},
});

const connected = connect(mapStateToProps)(radium(InteractiveMap));
export { connected };


// WEBPACK FOOTER //
// ./assets/pois/components/interactiveMap/index.jsx